import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Autocomplete, Box, Button, Grid, TextField, Typography, useMediaQuery } from "@mui/material";
import ButtonBack from "../../utils/ButtonBack";
import ErrorModal from "../../utils/ErrorModal";
import ThemedDatePicker from "../../utils/ThemedDatePicker";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";
const ERROR = "#C42B24";

// Section accent dots
const SEC_COLORS = {
  fields: "#2F7FD1",
  details: "#12A08F",
};

/* ---------- shared visual styles (values only — no logic) ---------- */
const card = { background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 14, marginBottom: 14 };
const cardHead = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "13px 16px", borderBottom: `1px solid ${LINE}` };
const cardTitle = { display: "flex", alignItems: "center", gap: 9, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: INK_2 };
const dot = (c) => ({ width: 6, height: 6, flex: "0 0 6px", borderRadius: "50%", background: c });
const cardMeta = { fontFamily: FONT, fontSize: 12, color: MUTED };
const labelStyle = { display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: INK };
const req = { color: ERROR, marginLeft: 3 };
const inputStyle = (hasError, mono) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 36,
  padding: "0 12px",
  margin: 0,
  background: FIELD_BG,
  border: `1px solid ${hasError ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  fontFamily: mono ? MONO : FONT,
  fontSize: mono ? 13 : 13.5,
  color: INK,
  outline: "none",
});
const errorStyle = { display: "block", margin: "4px 0 0", fontFamily: FONT, fontSize: 11.5, fontWeight: 500, color: ERROR };
const primaryBtn = {
  height: "38px !important", minHeight: "38px !important", px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: `${PRIMARY} !important`, color: "#FFFFFF !important", border: "0 !important",
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none", whiteSpace: "nowrap",
  "&:hover": { background: `${PRIMARY_DARK} !important` }, "&.Mui-disabled": { opacity: 0.6 },
};
const ghostBtn = {
  height: "38px !important", minHeight: "38px !important", px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: "#FFFFFF !important", color: `${INK} !important`, border: `1px solid ${FIELD_EDGE} !important`,
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none",
  "&:hover": { background: "#F4F7FA !important" },
};
const cols = (n) => ({
  display: "grid",
  gap: "16px 12px",
  gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(2, minmax(0,1fr))", lg: `repeat(${n}, minmax(0,1fr))` },
});

// Scoped resets so global App.css rules (form, border-container,
// phone-container, inputs, labels) can't squeeze or restyle this page.
const SCOPED_CSS = `
.dc-org .border-container,.dc-org form.form,.dc-org .dashboard-container,.dc-org .add-forms{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;box-shadow:none!important;background:transparent!important;border:0!important;box-sizing:border-box!important;}
.dc-org .dc-card{width:100%!important;max-width:none!important;margin:0 0 14px 0!important;padding:0!important;box-shadow:none!important;box-sizing:border-box!important;}
.dc-org .dc-card label{width:auto!important;margin-top:0!important;font-size:12px!important;line-height:1.4!important;}
.dc-org .dc-card input:not([type=checkbox]){width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;height:36px!important;font-size:13.5px!important;}
.dc-org .dc-card .MuiInputBase-root input{height:auto!important;width:auto!important;flex:1 1 60px;}
.dc-org .dc-card .MuiAutocomplete-root,.dc-org .dc-card .MuiInputBase-root{width:100%!important;max-width:none!important;}
.dc-org .dc-card .MuiFormControlLabel-root{width:100%!important;}
.dc-org .dc-card .phone-container{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;}
.dc-org .dc-card input.dc-bare{height:100%!important;border:0!important;background:transparent!important;flex:1 1 auto;min-width:0;}
.dc-org .dc-card input::placeholder{color:#8A97A8;opacity:1;}
.dc-org .dc-cc *{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;font-size:13.5px!important;color:#0F1B2A;}
.dc-org .dc-card p.error{margin:4px 0 0!important;}
.dc-org .dc-head-btns{display:flex!important;flex-direction:row!important;margin:0!important;padding:0!important;align-items:center!important;align-self:flex-end!important;}
.dc-org .dc-head-btns .MuiButton-root,.dc-org .dc-head-btns button{margin:0!important;height:38px!important;min-height:38px!important;max-height:38px!important;padding-top:0!important;padding-bottom:0!important;line-height:1!important;position:static!important;inset:auto!important;top:auto!important;bottom:auto!important;transform:none!important;translate:none!important;align-self:center!important;margin-block:0!important;vertical-align:middle!important;box-sizing:border-box!important;}
`;

// Extra resets for this page (textarea + step badge)
const PAGE_CSS = `
.dc-org .dc-card textarea{width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;font-size:13.5px!important;font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;}
.dc-org .dc-card textarea::placeholder{color:#8A97A8;opacity:1;}
.dc-org .dc-card label.project-checkbox{margin:0!important;width:100%!important;font-size:13px!important;}
.dc-org .form-step{margin:0!important;}
`;

const stepBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  borderRadius: "50%",
  background: "#EAF2FB",
  border: "1px solid #C9DEF3",
  color: "#1B4F82",
  fontFamily: MONO,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0,
  flexShrink: 0,
  margin: 0,
};
const acSx = {
  margin: 0,
  "& .MuiInputBase-root": {
    minHeight: 36,
    fontFamily: FONT,
    fontSize: "13.5px",
    borderRadius: "8px",
    padding: "0 8px !important",
    background: FIELD_BG,
  },
  "& .MuiInputBase-root.Mui-focused": { background: "#FFFFFF" },
  "& .MuiInputBase-input": { padding: "7px 4px !important" },
  "& input::placeholder": { color: "#8A97A8", opacity: 1 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
  "& .MuiInputBase-root.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ERROR },
  "& .MuiFormHelperText-root": { display: "none" },
};

const ALL_FIELDS = [
  { label: "Project Name", name: "projectName", type: "text" },
  { label: "Builder Name", name: "builder", type: "text" },
  { label: "Location", name: "location", type: "text" },
  { label: "RERA Number", name: "reraNumber", type: "text" },
  { label: "Total Units", name: "totalUnits", type: "number" },
  { label: "Launch Date", name: "launchDate", type: "date" },
  { label: "Possession Date", name: "possessionDate", type: "date" },
  { label: "Status", name: "status", type: "text" },
  { label: "Base Price", name: "basePrice", type: "number" },
  { label: "Website URL", name: "websiteUrl", type: "text" },
  { label: "Brochure URL", name: "brochureUrl", type: "text" },
  { label: "Description", name: "description", type: "textarea" },
];

// Placeholders (display only)
const PLACEHOLDERS = {
  projectName: "Project name",
  builder: "Builder / developer",
  location: "Area, city",
  reraNumber: "P52100012345",
  totalUnits: "0",
  status: "Under construction",
  basePrice: "0",
  websiteUrl: "https://",
  brochureUrl: "https://",
  description: "Short description of the project...",
};

const EditProject = () => {
  const { projectId } = useParams();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [teams, setTeams] = useState();
  const [teamId, setTeamId] = useState();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const orgId = JSON.parse(localStorage.getItem("org"));
  const isMobile = useMediaQuery("(max-width: 750px)");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/project?projectId=${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );

        setProject(res.data);
        setTeamId(res.data?.team?.teamId);

        // Auto-select fields that already have values
        const preSelectedFields = ALL_FIELDS.filter(
          (field) =>
            res.data[field.name] !== null &&
            res.data[field.name] !== undefined &&
            res.data[field.name] !== "",
        ).map((f) => f.name);

        setSelectedFields(preSelectedFields);
      } catch (error) {
        console.error(error);
      }
    };

    fetchProject();

    const getTeams = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/team/all?orgId=${orgId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setTeams(res.data);
      } catch (error) {
        console.log(error.response.data.Exception || "Access denied");
      }
    };
    getTeams();
  }, [projectId, storedUser.accessToken]);

  const handleInputChange = (name, value) => {
    setProject((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const REQUIRED_FIELDS = ["projectName", "builder", "location"];

  const toggleFieldSelection = (fieldName) => {
    if (REQUIRED_FIELDS.includes(fieldName)) return;
    setSelectedFields((prev) =>
      prev.includes(fieldName)
        ? prev.filter((f) => f !== fieldName)
        : [...prev, fieldName],
    );
  };

  const handleSubmit = async () => {
    const newErrors = {};

    // Required fields validation
    REQUIRED_FIELDS.forEach((field) => {
      if (!project[field] || project[field].toString().trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    if (!teamId) {
      newErrors.teamId = "This field is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const payload = {
      ...project,
      orgId: orgId,
      selectedFields: selectedFields,
      teamId: teamId,
    };

    try {
      setLoading(true)
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/project?projectId=${project.projectId}`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      navigate("/dashboard/projects");
    } catch (error) {
      console.error(error);
      // FIX: tell the user the save failed (was silent before)
      setModalMsg(error?.response?.data?.Exception || "Failed to update project");
      setModalOpen(true);
    } finally {
      setLoading(false)
    }
  };

  if (!project)
    return (
      <p style={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 500, color: MUTED, padding: "24px 0" }}>
        Loading project...
      </p>
    );

  /* ---------- markup helpers (same handlers as before) ---------- */
  const section = (id, num, title, meta, children, n = 3, hint) => (
    <section key={id} className="dc-card" style={card}>
      <div style={cardHead}>
        <span style={cardTitle}>
          <span className="form-step" style={stepBadgeStyle}>{num}</span>
          {title}
        </span>
        <span style={cardMeta}>{meta}</span>
      </div>
      <Box sx={{ p: "16px 16px 18px" }}>
        {hint && (
          <div style={{ margin: "-4px 0 12px", fontFamily: FONT, fontSize: 12.5, color: MUTED }}>{hint}</div>
        )}
        <Box sx={cols(n)}>{children}</Box>
      </Box>
    </section>
  );

  const fieldChip = (field) => {
    const on = selectedFields.includes(field.name);
    const locked = REQUIRED_FIELDS.includes(field.name);
    return (
      <label
        key={field.name}
        className={`project-checkbox ${
          selectedFields.includes(field.name) ? "checkbox-active" : ""
        }`}
        title={locked ? "Always included" : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          boxSizing: "border-box",
          height: 36,
          padding: "0 12px",
          margin: 0,
          background: on ? "#EAF2FB" : FIELD_BG,
          border: `1px solid ${on ? "#8FC3E6" : FIELD_EDGE}`,
          borderRadius: 8,
          color: on ? "#1B4F82" : INK_2,
          fontFamily: FONT,
          fontSize: 13,
          fontWeight: on ? 600 : 500,
          lineHeight: 1.3,
          cursor: locked ? "not-allowed" : "pointer",
          userSelect: "none",
          transition: "background .15s, border-color .15s",
        }}
      >
        <input
          type="checkbox"
          checked={selectedFields.includes(field.name)}
          onChange={() => toggleFieldSelection(field.name)}
          style={{ width: "auto", display: "none" }}
        />
        <span
          aria-hidden="true"
          style={{
            flex: "0 0 14px",
            width: 14,
            height: 14,
            borderRadius: 3,
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: on ? (locked ? "#8494A8" : PRIMARY) : "#FFFFFF",
            border: `1.5px solid ${on ? (locked ? "#8494A8" : PRIMARY) : "#9AA7B8"}`,
            color: "#FFFFFF",
            fontSize: 10,
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {on ? "✓" : ""}
        </span>
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {field.label}
        </span>
      </label>
    );
  };

  const detailField = (fieldName) => {
    const field = ALL_FIELDS.find((f) => f.name === fieldName);

    if (!field) return null;

    const err = errors[field.name];
    const isNum = field.type === "number";

    return (
      <Box key={field.name} sx={field.type === "textarea" ? { gridColumn: "1 / -1" } : undefined}>
        <label style={labelStyle}>
          {field.label}
          {REQUIRED_FIELDS.includes(field.name) && <span style={req}>*</span>}
        </label>

        {field.type === "textarea" ? (
          <textarea
            className={`${err && "input-error"}`}
            value={project[field.name] || ""}
            placeholder={PLACEHOLDERS[field.name] || ""}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            style={{
              ...inputStyle(!!err),
              height: "auto",
              minHeight: 80,
              padding: "9px 12px",
              lineHeight: 1.5,
              resize: "vertical",
            }}
            rows={3}
          />
        ) : field.type === "date" ? (
          <ThemedDatePicker
            name={field.name}
            value={project[field.name] || ""}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            error={!!err}
          />
        ) : (
          <input
            className={`${err && "input-error"}`}
            type={field.type}
            value={project[field.name] || ""}
            placeholder={PLACEHOLDERS[field.name] || ""}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            style={inputStyle(!!err, isNum)}
          />
        )}
        {err && (
          <span className="error-text" style={errorStyle}>
            {err}
          </span>
        )}
      </Box>
    );
  };

  const teamField = (
    <Box key="teamId">
      <label htmlFor="team" style={labelStyle}>
        Assign Team<span style={req}>*</span>
      </label>
      <Autocomplete
        name="team"
        className={`${errors.teamId && "input-error"}`}
        options={teams || []}
        getOptionLabel={(option) => option.name || ""}
        value={teams?.find((t) => t.teamId === teamId) || null}
        onChange={(e, value) => {
          setTeamId(value?.teamId || "");
        }}
        isOptionEqualToValue={(option, value) =>
          option.teamId === value.teamId
        }
        renderInput={(params) => (
          <TextField
            {...params}
            name="teamId"
            placeholder="Select Team"
            error={!!errors.teamId}
            helperText={errors.teamId}
          />
        )}
        sx={acSx}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "12px",
              border: `1px solid ${LINE}`,
              boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
              "& .MuiAutocomplete-option": {
                fontFamily: FONT,
                fontSize: 13.5,
                borderRadius: "8px",
                mx: 0.75,
              },
            },
          },
        }}
      />
      {errors.teamId && (
        <span className="error-text" style={errorStyle}>{errors.teamId}</span>
      )}
    </Box>
  );

  const saveButton = (
    <Button
      className="btn-contained"
      onClick={handleSubmit}
      disabled={loading}
      sx={primaryBtn}
    >
      {loading ? "Saving..." : "Save Project"}
    </Button>
  );

  // Details: selected fields (textarea last so it spans the full row), then team
  const detailNames = [
    ...selectedFields.filter((n) => n !== "description"),
    ...(selectedFields.includes("description") ? ["description"] : []),
  ];
  const goProjects = () => navigate("/dashboard/projects");

  return (
    <>
      <style>{SCOPED_CSS + PAGE_CSS}</style>
      <div className="dc-org" style={{ fontFamily: FONT, color: INK, textAlign: "left", padding: isMobile ? "4px 0 88px" : "4px 0 32px" }}>
        {/* ---------------- PAGE HEADER ---------------- */}
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
          <div style={{ minWidth: 0 }}>
            <button
              type="button"
              onClick={goProjects}
              style={{ border: 0, background: "transparent", padding: 0, marginBottom: 14, cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 600, color: PRIMARY }}
            >
              ← Projects
            </button>
            <Typography
              component="h1"
              sx={{ m: 0, fontFamily: FONT, fontSize: isMobile ? 24 : 28, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, color: INK }}
            >
              Edit project
            </Typography>
            <div style={{ marginTop: 6, fontSize: 13.5, color: INK_2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {[project.projectName, project.location].filter(Boolean).join(" · ") || "Project details"}
            </div>
          </div>
          {!isMobile && (
            <Box className="dc-head-btns" sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: "0 0 auto" }}>
              <Button className="btn-outlined" onClick={goProjects} sx={ghostBtn}>
                Cancel
              </Button>
              {saveButton}
            </Box>
          )}
        </Box>

        <div className="add-forms">
          {/* ---------------- 1. FIELDS ---------------- */}
          {section(
            "fields",
            1,
            "Select fields for project",
            `${selectedFields.length} of ${ALL_FIELDS.length} selected`,
            ALL_FIELDS.map(fieldChip),
            6,
            "Project Name, Builder Name and Location are always included.",
          )}

          {/* ---------------- 2. DETAILS ---------------- */}
          {section(
            "details",
            2,
            "Project details",
            "4 required",
            [...detailNames.map(detailField), teamField],
            3,
          )}
        </div>

        {/* ---------------- MOBILE ACTION BAR ---------------- */}
        {isMobile && (
          <Box
            className="btn-container"
            sx={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30, display: "flex", alignItems: "center", gap: 1.25,
              p: "10px 16px", background: "#FFFFFF", borderTop: `1px solid ${LINE}`, boxShadow: "0 -8px 24px -16px rgba(15,27,42,0.3)",
            }}
          >
            <ButtonBack handleBack={() => navigate("/dashboard/projects")} />
            <Box sx={{ flex: 1, display: "flex", "& > *": { flex: 1 } }}>{saveButton}</Box>
          </Box>
        )}

        <ErrorModal
          open={modalOpen}
          handleClose={() => setModalOpen(false)}
          msg={modalMsg}
          type="error"
        />
      </div>
    </>
  );
};

export default EditProject;